import { AIBadgeScore, Card, DateFormat, Divider, Icon, Text } from "components/common"
import { t } from "i18next"
import { Flex } from "antd"
import { IconText, Separator, TitleDescription } from "components/custom"
import { convertCamelCaseToReadable, getCurrencySymbol, joinBytItems, localisedValue } from "utils"
import theme from "utils/themeVars"

export const schemaRules = schema => [
  {
    validator: async ({ field }, value) => {
      await schema.validateSyncAt(field, { [field]: value })
    },
  },
]

export const milestoneColorMapper = {
  client_creation: "#0F7187",
  contract_cancelled: "#A52F00",
  contract_signed: "#269008",
  phone_call: "#5B92D8",
  audit_review: "#F28408",
  whatsapp_chat: "#5B92D8",
  meeting_done: "#3E4B8B",
  payment_added: "#269008",
  payment_rejected: "#A52F00",
}

const meetingTypeIconMapper = {
  company_office: "IoBriefcaseOutline",
  site_office: "MdOutlineLocationOn",
}

export const getMilestoneTooltip = item => {
  if (!item?.data?.milestones?.length) {
    return null
  }

  return (
    <Flex vertical align="flex-start" className="mbe-12" gap="8px">
      <Divider style={{ marginBlock: "0px" }} />
      <Text className={"semiBold"}>{t("Activity")}</Text>
      <Card variant="fill" style={{ "--bg-color": "#FAFAFA", width: "100%" }} bodyStyle={{ padding: 12 }}>
        {item?.data?.milestones?.map((data, index) => (
          <>
            {(() => {
              switch (data.milestoneTypeKey) {
                case "client_creation":
                  return (
                    <Flex gap="4px" vertical align="flex-start">
                      <IconText
                        icon={"FiUser"}
                        title={localisedValue({ object: data, key: "milestoneTypeName" }) || "-"}
                        titleProps={{ className: "fs12 semiBold" }}
                        iconProps={{ color: theme["text-color-secondary"], size: 14 }}
                      />
                      <Text type={"secondary"} className={"fs12"}>
                        {data?.milestoneMetadata?.name || "-"}
                      </Text>
                      <Flex gap="4px" wrap="wrap" align={"center"}>
                        {joinBytItems(
                          [
                            <DateFormat
                              date={item?.data?.analysisDate}
                              showTime
                              dateClassName={"gray-900 fs12"}
                              color={""}
                            />,
                            <Text type={"secondary"} className={"fs12"}>
                              {convertCamelCaseToReadable(data?.milestoneMetadata?.platform)}
                            </Text>,
                            <Text type={"secondary"} className={"fs12"}>
                              {convertCamelCaseToReadable(data?.milestoneMetadata?.source)}
                            </Text>,
                          ],
                          <Separator />
                        )}
                      </Flex>
                    </Flex>
                  )
                case "contract_signed":
                case "contract_cancelled":
                  return (
                    <>
                      <TitleDescription
                        title={
                          <IconText
                            icon={data.milestoneTypeKey === "contract_cancelled" ? "GiOpenBook" : "IoMdDocument"}
                            title={localisedValue({ object: data, key: "milestoneTypeName" })}
                            titleProps={{ className: "fs12 semiBold" }}
                            iconProps={{ color: theme["text-color-secondary"], size: 12 }}
                          />
                        }
                        titleClassName="semiBold fs12"
                        gapSpace={4}
                        description={
                          <Text type={"secondary"} className="fs12">
                            {data?.milestoneMetadata?.description}
                          </Text>
                        }
                      />
                    </>
                  )
                case "phone_call":
                  return (
                    <Flex gap="4px" vertical>
                      <Flex justify={"space-between"}>
                        <IconText
                          icon="MdCall"
                          title={data?.milestoneTypeName || "-"}
                          titleProps={{ className: "fs12 semiBold" }}
                          iconProps={{ color: theme["text-color-secondary"], size: 14 }}
                        />
                        <AIBadgeScore
                          totalScore={data?.milestoneMetadata?.score}
                          title={"Score:"}
                          titleClassName={"fs12"}
                          padding={"pi-0"}
                          buttonProps={{
                            style: { height: 18 },
                            size: "small",
                            shape: "round",
                            color: "#FAFAFA",
                            icon: null,
                          }}
                        />
                      </Flex>
                      <Text type={"secondary"} className={"fs12"}>
                        {data?.milestoneMetadata?.summary}
                      </Text>
                    </Flex>
                  )
                case "audit_review":
                  return (
                    <>
                      <Flex gap="4px" vertical>
                        <IconText
                          icon="IoFlag"
                          title={data?.milestoneTypeName || "-"}
                          titleProps={{ className: "fs12 semiBold" }}
                          iconProps={{ color: theme["text-color-secondary"], size: 12 }}
                        />
                        <DateFormat
                          date={data?.milestoneMetadata?.reviewDate}
                          showTime
                          dateClassName={"gray-900 fs12"}
                          color={""}
                        />
                        <Text type={"secondary"} className={"fs12"}>
                          {t("Details: ") + data?.milestoneMetadata?.feedback}
                        </Text>
                      </Flex>
                    </>
                  )
                case "whatsapp_chat":
                  return (
                    <Flex gap="4px" vertical>
                      <Flex justify={"space-between"}>
                        <IconText
                          icon="RiWhatsappFill"
                          title={data?.milestoneTypeName || "-"}
                          titleProps={{ className: "fs12 semiBold" }}
                          iconProps={{ color: theme["primary-color"], size: 12 }}
                        />
                        <AIBadgeScore
                          totalScore={data?.milestoneMetadata?.score}
                          title={"Score:"}
                          titleClassName={"fs12"}
                          padding={"pi-0"}
                          buttonProps={{
                            style: { height: 18 },
                            size: "small",
                            shape: "round",
                            color: "#FAFAFA",
                            icon: null,
                          }}
                        />
                      </Flex>
                      <Text type={"secondary"} className={"fs12"}>
                        {data?.milestoneMetadata?.analysis}
                      </Text>
                    </Flex>
                  )
                case "meeting_done":
                  return (
                    <>
                      <Flex gap="4px" vertical>
                        <IconText
                          icon={meetingTypeIconMapper[data?.milestoneMetadata?.key] ?? "FaUsers"}
                          title={data?.milestoneTypeName || "-"}
                          titleProps={{ className: "fs12 semiBold" }}
                          iconProps={{ color: theme["text-color-secondary"], size: 14 }}
                        />
                        <Flex wrap="wrap" align={"center"}>
                          {joinBytItems(
                            [
                              <Text type={"secondary"} className={"fs12"}>
                                <Icon icon={"AiOutlineCalendar"} />
                                {localisedValue({ object: data?.milestoneMetadata, key: "name" })}
                              </Text>,
                              <DateFormat
                                date={data?.milestoneMetadata?.meetingDate}
                                showTime
                                dateClassName={"gray-900 fs12"}
                                color={""}
                              />,
                            ],
                            <Separator />
                          )}
                        </Flex>
                        <Text type={"secondary"} className={"fs12"}>
                          {t("Remarks: ") + localisedValue({ object: data?.milestoneMetadata, key: "description" })}
                        </Text>
                      </Flex>
                    </>
                  )
                case "payment_rejected":
                case "payment_added":
                  return (
                    <>
                      <Flex gap="4px" vertical>
                        <IconText
                          icon="FaMoneyBill"
                          title={data?.milestoneTypeName || "-"}
                          titleProps={{ className: "fs12 semiBold" }}
                          iconProps={{ color: theme["text-color-secondary"], size: 14 }}
                        />
                        <Text type={"secondaryLight"} className={"fs12"}>
                          {data?.milestoneMetadata?.paymentType +
                            (data.milestoneTypeKey === "payment_added" ? t(" Received: ") : t(" Rejected: "))}
                          <Text type={"secondary"} className={"fs12"}>
                            {data?.milestoneMetadata?.amount}
                            {getCurrencySymbol()}
                          </Text>
                        </Text>
                        <Text type={"secondary"} className={"fs12"}>
                          {localisedValue({ object: data?.milestoneMetadata, key: "description" })}
                        </Text>
                      </Flex>
                    </>
                  )
                default:
                  return null
              }
            })()}
            {!!item?.data?.milestones?.[index + 1] && <Divider style={{ marginBlock: 12 }} />}
          </>
        ))}
      </Card>
    </Flex>
  )
}
